"use client";
import { useState, useEffect, useRef, useCallback } from "react";
import { isValidUrl, normalizeUrl, isValidEmail } from "../lib/validation";

/* ── DESIGN TOKENS (match AIAdvisors.jsx) ── */
const BG = "#09090b";
const TEXT = "#EDEDED";
const TEXT_MUTED = "#a0a0a0";
const TEAL = "#4FCDC4";
const AMBER = "#f59e0b";
const RED = "#ef4444";
const CARD_BORDER = "rgba(255,255,255,0.04)";

const GREEN = "#22c55e";

/* ── HELPERS ── */
function scoreColor(score) {
  if (score >= 80) return GREEN;
  if (score >= 61) return AMBER;
  return RED;
}

function scoreLabelData(score) {
  if (score >= 90) return { word: "Excellent.", color: GREEN, rest: " Your site is well-optimized for AI search." };
  if (score >= 80) return { word: "Good.", color: GREEN, rest: " A few improvements could boost your AI visibility." };
  if (score >= 61) return { word: "Needs improvement.", color: AMBER, rest: " AI platforms may be overlooking your content." };
  return { word: "Significant gaps.", color: RED, rest: " Your site needs optimization for AI search." };
}

/* ── ANIMATED COUNT-UP (local to QuickScan) ── */
function QSCountUp({ to, duration = 1200, onDone }) {
  const [display, setDisplay] = useState(0);
  const animated = useRef(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (animated.current) return;
    animated.current = true;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(t >= 1 ? to : Math.round(eased * (to - 1)));
      if (t < 1) requestAnimationFrame(step);
      else if (onDoneRef.current) onDoneRef.current();
    };
    requestAnimationFrame(step);
  }, [to, duration]);

  return <>{display}</>;
}

/* ── SCORE GAUGE (SVG with animation) ── */
function ScoreGauge({ score, size = 140, onDone }) {
  const r = (size - 14) / 2;
  const circ = 2 * Math.PI * r;
  const finalOffset = circ * (1 - score / 100);
  const color = scoreColor(score);
  const center = size / 2;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Start with full offset, then animate to final on next frame
    requestAnimationFrame(() => setMounted(true));
  }, []);

  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={center} cy={center} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="7" />
        <circle
          cx={center} cy={center} r={r} fill="none"
          stroke={color} strokeWidth="7"
          strokeDasharray={circ} strokeDashoffset={mounted ? finalOffset : circ}
          strokeLinecap="round" transform={`rotate(-90 ${center} ${center})`}
          style={{ transition: mounted ? "stroke-dashoffset 1.2s cubic-bezier(.16,1,.3,1)" : "none" }}
        />
      </svg>
      <div style={{
        position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <span style={{ fontSize: 48, fontWeight: 700, color: TEXT, fontFamily: "var(--display)", letterSpacing: "-0.021em" }}>
          <QSCountUp to={score} duration={1200} onDone={onDone} />
        </span>
      </div>
    </div>
  );
}

/* ── LOADING STEPS ── */
const SCAN_STEPS = [
  { text: "Running Quick Audit...", delay: 0 },
  { text: "Fetching homepage and key pages...", delay: 1200 },
  { text: "Checking for Cloudflare blocking...", delay: 2400 },
  { text: "Analyzing robots.txt and llms.txt...", delay: 3600 },
  { text: "Scanning schema markup...", delay: 4800 },
  { text: "Evaluating content structure...", delay: 6000 },
  { text: "Checking authority signals...", delay: 7200 },
  { text: "Scoring and generating recommendations...", delay: 8400 },
];

const MIN_LOADING_MS = 9600;

/* ══════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════ */
export default function QuickScan({ isMobile }) {
  // States: "form" | "loading" | "results" | "error"
  const [phase, setPhase] = useState("form");
  const [url, setUrl] = useState("");
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [cached, setCached] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [emailInput, setEmailInput] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const containerRef = useRef(null);
  const loadingStartRef = useRef(0);
  const stepTimersRef = useRef([]);

  // Clean up step timers on unmount
  useEffect(() => {
    return () => stepTimersRef.current.forEach(clearTimeout);
  }, []);

  const startScan = useCallback(async () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    if (!isValidUrl(trimmed)) {
      setError("Please enter a valid URL (e.g. example.com)");
      return;
    }

    setPhase("loading");
    setError("");
    setResults(null);
    setCached(false);
    setCurrentStep(0);
    setEmailSent(false);
    setEmailInput("");
    loadingStartRef.current = Date.now();

    // Start step timers
    stepTimersRef.current.forEach(clearTimeout);
    stepTimersRef.current = SCAN_STEPS.map((step, i) =>
      setTimeout(() => setCurrentStep(i), step.delay)
    );

    try {
      const res = await fetch("/api/quick-scan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: trimmed }),
      });

      const data = await res.json();

      if (!res.ok && !data.score && !data.cachedResults) {
        // Wait minimum time before showing error
        const elapsed = Date.now() - loadingStartRef.current;
        const remaining = Math.max(0, 3000 - elapsed);
        setTimeout(() => {
          stepTimersRef.current.forEach(clearTimeout);
          setError(data.error || "Something went wrong. Please try again.");
          setPhase("error");
        }, remaining);
        return;
      }

      if (data.cached) setCached(true);

      // Wait until minimum loading time has passed
      const elapsed = Date.now() - loadingStartRef.current;
      const remaining = Math.max(0, MIN_LOADING_MS - elapsed);

      setTimeout(() => {
        stepTimersRef.current.forEach(clearTimeout);
        setResults(data);
        setPhase("results");
      }, remaining);
    } catch {
      const elapsed = Date.now() - loadingStartRef.current;
      const remaining = Math.max(0, 3000 - elapsed);
      setTimeout(() => {
        stepTimersRef.current.forEach(clearTimeout);
        setError("Something went wrong. Please try again.");
        setPhase("error");
      }, remaining);
    }
  }, [url]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") startScan();
  };

  const handleEmailSubmit = async () => {
    if (!isValidEmail(emailInput)) return;
    setEmailSending(true);
    try {
      await fetch("/api/quick-scan/email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: emailInput.trim(),
          url: results?.url || url,
          score: results?.score,
          technical: results?.technical?.score,
          content: results?.content?.score,
          authority: results?.authority?.score,
          recommendations: (results?.recommendations || []).slice(0, 3).map((r) => ({
            title: r.title,
            description: r.description,
          })),
        }),
      });
      setEmailSent(true);
    } catch {
      // Silent fail for email capture
    }
    setEmailSending(false);
  };

  /* ── FORM STATE ── */
  if (phase === "form") {
    return (
      <div ref={containerRef} style={{
        animation: "qsFadeIn 0.3s ease",
      }}>
        <div style={{
          display: "flex", flexDirection: isMobile ? "column" : "row",
          justifyContent: "center",
          gap: 12, margin: "32px auto 0",
          alignItems: "stretch",
        }}>
          <input
            id="quick-audit-url"
            name="url"
            aria-label="Website URL"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter your website URL"
            autoComplete="url"
            style={{
              flex: 1, maxWidth: isMobile ? undefined : 300,
              width: isMobile ? "100%" : undefined,
              minHeight: 48,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 10, padding: "0 16px",
              color: TEXT, fontSize: 16, fontFamily: "var(--body)",
              outline: "none",
            }}
          />
          <button
            onClick={startScan}
            disabled={!url.trim()}
            style={{
              minHeight: 48, padding: "0 28px",
              width: isMobile ? "100%" : undefined,
              background: url.trim() ? TEAL : "rgba(255,255,255,0.06)",
              color: url.trim() ? "#000" : TEXT_MUTED, fontWeight: 600, fontSize: 16,
              fontFamily: "var(--body)", border: "none",
              borderRadius: 10, cursor: url.trim() ? "pointer" : "default",
              transition: "background 0.2s",
              whiteSpace: "nowrap",
            }}
          >
            Quick Audit
          </button>
        </div>
      </div>
    );
  }

  /* ── LOADING STATE ── */
  if (phase === "loading") {
    return (
      <div style={{
        maxWidth: 560, margin: "40px auto 0", textAlign: "center",
        animation: "qsFadeIn 0.3s ease",
      }}>
        {/* Pulsing sparkle */}
        <div style={{
          fontSize: 32, color: TEAL, marginBottom: 28,
          animation: "qsPulse 2s ease-in-out infinite",
        }}>
          &#10022;
        </div>
        {/* Step messages */}
        <div style={{ minHeight: 200 }}>
          {SCAN_STEPS.map((step, i) => (
            <div
              key={i}
              style={{
                fontSize: 15, fontFamily: "var(--body)", lineHeight: 2,
                color: i === currentStep ? TEXT : i < currentStep ? "#666666" : "transparent",
                transform: i <= currentStep ? "translateY(0)" : "translateY(4px)",
                opacity: i <= currentStep ? 1 : 0,
                transition: "color 0.3s, opacity 0.4s ease, transform 0.4s ease",
              }}
            >
              {step.text}
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* ── ERROR STATE ── */
  if (phase === "error") {
    return (
      <div style={{
        maxWidth: 560, margin: "40px auto 0", textAlign: "center",
        animation: "qsFadeIn 0.3s ease",
      }}>
        <p style={{
          fontFamily: "var(--body)", fontSize: 16, color: AMBER,
          margin: "0 0 20px", lineHeight: 1.6,
        }}>
          {error}
        </p>
        <button
          onClick={() => { setPhase("form"); setError(""); }}
          style={{
            height: 44, padding: "0 28px", background: TEAL, color: "#000",
            fontWeight: 600, fontSize: 15, fontFamily: "var(--body)",
            border: "none", borderRadius: 10, cursor: "pointer",
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  /* ── RESULTS STATE ── */
  if (phase === "results" && results) {
    const recs = (results.recommendations || []).slice(0, 3);

    return (
      <ResultsView results={results} recs={recs} cached={cached} isMobile={isMobile}
        emailInput={emailInput} setEmailInput={setEmailInput} emailSent={emailSent}
        emailSending={emailSending} handleEmailSubmit={handleEmailSubmit} url={url} />
    );
  }

  return null;
}

/* ── RESULTS VIEW (separated so hooks work at top level) ── */
function ResultsView({ results, recs, cached, isMobile, emailInput, setEmailInput, emailSent, emailSending, handleEmailSubmit, url }) {
  const [gaugeDone, setGaugeDone] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const [showRecs, setShowRecs] = useState(false);

  useEffect(() => {
    if (!gaugeDone) return;
    const t1 = setTimeout(() => setShowLabel(true), 200);
    return () => clearTimeout(t1);
  }, [gaugeDone]);

  useEffect(() => {
    if (!showLabel) return;
    const t2 = setTimeout(() => setShowRecs(true), 600);
    return () => clearTimeout(t2);
  }, [showLabel]);

  const label = scoreLabelData(results.score);

  return (
      <div style={{
        maxWidth: 720, margin: "40px auto 0",
        animation: "qsSlideUp 0.5s ease",
      }}>
        {cached && (
          <p style={{
            fontFamily: "var(--body)", fontSize: 14, color: AMBER,
            textAlign: "center", margin: "0 0 20px",
          }}>
            This site was recently scanned. Here are the cached results.
          </p>
        )}

        {/* A. Overall Score */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ display: "inline-block" }}>
            <ScoreGauge score={results.score} size={isMobile ? 120 : 140} onDone={() => setGaugeDone(true)} />
          </div>
          <p style={{
            fontFamily: "var(--display)", fontSize: 16, fontWeight: 600,
            color: TEXT_MUTED, margin: "12px 0 6px",
          }}>
            Your AEO Score
          </p>
          <p style={{
            fontFamily: "var(--body)", fontSize: 16, color: TEXT,
            margin: 0, maxWidth: 480, marginLeft: "auto", marginRight: "auto",
            opacity: showLabel ? 1 : 0,
            transition: showLabel ? "opacity 0.4s ease" : "none",
          }}>
            <span style={{ color: label.color, fontWeight: 600 }}>{label.word}</span>{label.rest}
          </p>
        </div>

        {/* B. Category Scores */}
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
          gap: 12, marginBottom: 40,
        }}>
          {[
            { label: "Technical", score: results.technical?.score },
            { label: "Content", score: results.content?.score },
            { label: "Authority", score: results.authority?.score },
          ].map((cat) => (
            <div key={cat.label} style={{
              background: "rgba(255,255,255,0.02)",
              border: `1px solid ${CARD_BORDER}`,
              borderRadius: 12, padding: "20px 24px", textAlign: "center",
            }}>
              <div style={{
                fontFamily: "var(--body)", fontSize: 14, color: TEXT_MUTED,
                marginBottom: 6,
              }}>
                {cat.label}
              </div>
              <div style={{
                fontFamily: "var(--display)", fontSize: 32, fontWeight: 700,
                color: scoreColor(cat.score), letterSpacing: "-0.016em",
              }}>
                <QSCountUp to={cat.score} duration={1200} /><span style={{ fontSize: 16, color: TEXT_MUTED, fontWeight: 400 }}>/100</span>
              </div>
            </div>
          ))}
        </div>

        {/* C. Recommendations */}
        {recs.length > 0 && (
          <div style={{ marginBottom: 40 }}>
            <h3 style={{
              fontFamily: "var(--display)", fontSize: 22, fontWeight: 600,
              color: TEXT, letterSpacing: "-0.023em", margin: "0 0 16px",
            }}>
              Top recommendations
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {recs.map((rec, i) => (
                <div key={i} style={{
                  opacity: showRecs ? 1 : 0,
                  transform: showRecs ? "translateY(0)" : "translateY(8px)",
                  transition: showRecs ? `opacity 0.4s ease ${i * 0.15}s, transform 0.4s ease ${i * 0.15}s` : "none",
                }}>
                  <p style={{
                    fontFamily: "var(--body)", fontSize: 15, fontWeight: 600,
                    color: TEXT, margin: "0 0 4px",
                  }}>
                    {i + 1}. {rec.title}
                  </p>
                  <p style={{
                    fontFamily: "var(--body)", fontSize: 14, color: TEXT_MUTED,
                    margin: 0, lineHeight: 1.6, paddingLeft: 18,
                  }}>
                    {rec.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* D. Email Capture */}
        <div style={{
          background: "rgba(255,255,255,0.03)",
          border: `1px solid rgba(255,255,255,0.06)`,
          borderRadius: 14, padding: isMobile ? "24px 20px" : "28px 32px",
          marginBottom: 24, textAlign: "center",
        }}>
          {!emailSent ? (
            <>
              <p style={{
                fontFamily: "var(--body)", fontSize: 16, color: TEXT,
                margin: "0 0 16px", lineHeight: 1.6,
              }}>
                Email me the results and tips to improve my score.
              </p>
              <div style={{
                display: "flex", flexDirection: isMobile ? "column" : "row",
                gap: 12, maxWidth: 440, margin: "0 auto",
              }}>
                <input
                  id="quick-audit-email"
                  name="email"
                  aria-label="Email address"
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleEmailSubmit()}
                  placeholder="you@company.com"
                  autoComplete="email"
                  style={{
                    flex: 1, minHeight: 48,
                    width: isMobile ? "100%" : undefined,
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 10, padding: "0 16px",
                    color: TEXT, fontSize: 16, fontFamily: "var(--body)",
                    outline: "none",
                  }}
                />
                <button
                  onClick={handleEmailSubmit}
                  disabled={emailSending || !isValidEmail(emailInput)}
                  style={{
                    minHeight: 48, padding: "0 24px",
                    width: isMobile ? "100%" : undefined,
                    background: isValidEmail(emailInput) ? TEAL : "rgba(255,255,255,0.06)",
                    color: isValidEmail(emailInput) ? "#000" : TEXT_MUTED, fontWeight: 600, fontSize: 16,
                    fontFamily: "var(--body)", border: "none",
                    borderRadius: 10, cursor: emailInput.includes("@") ? "pointer" : "default",
                    transition: "background 0.2s", whiteSpace: "nowrap",
                  }}
                >
                  {emailSending ? "Sending..." : "Send Results"}
                </button>
              </div>
            </>
          ) : (
            <p style={{
              fontFamily: "var(--body)", fontSize: 16, color: TEAL,
              margin: 0, fontWeight: 500,
            }}>
              Results sent! Check your inbox.
            </p>
          )}
        </div>
      </div>
  );
}
